import * as React from 'react';
import { useState } from 'react';
import {
  Box,
  Button,
  Collapse,
  IconButton,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  MenuItem,
  FormControl,
  Select,
  SelectChangeEvent,
  ListItemText,
  Checkbox,
  OutlinedInput,
} from '@mui/material';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import BlackWhiteSwitch from '../components/common/BlackWhiteSwitch';
import CreateButtonWithModal from '../components/common/CreateButtonWithModal';
import Tooltip from '../components/common/Tooltip';

// Mock data creation
function createData(
  serviceName: string,
  countryMarket: string,
  subscriptionPlan: string,
  supplier: string,
  hotelName: string,
  roomType: string,
  mealPlan: string,
  firstDetails: Record<string, string>
) {
  return {
    serviceName,
    countryMarket,
    subscriptionPlan,
    supplier,
    hotelName,
    roomType,
    mealPlan,
    firstDetails
  };
}

// Sample rows
const rows = [
  createData(
    'Service A',
    'India',
    'Retail',
    'Supplier X',
    'Burj Khalifa',
    'Deluxe Room',
    'Breakfast',
    {
      'Refundable / Non-Refundable': 'Refundable',
    'Star Rating': '5 Star',
    'From Date': '2023-01-01',
    'To Date': '2023-12-31',
    'Booking Window': '2023-01-01 to 2023-12-31',
    'Minimum Stay': '2 Nights',
    'Maximum Stay': '7 Nights',
    'Applicability On': 'All',
    'Device Type': 'Mobile',
    'Services fees On': 'Yes',
    'Service fee Type': 'Percentage',
    'Service fee Value': '10',
    'Payment Method Surcharge': 'No',
    'Status': 'Active',
    }
  ),
];

// Subtable fields
const fareFields = ['Refundable / Non-Refundable', 'Star Rating', 'From Date', 'To Date', 'Booking Window', 'Minimum Stay', 'Maximum Stay'];
const airlineFields = ['Applicability On', 'Device Type', 'Services fees On', 'Service fee Type', 'Service fee Value', 'Payment Method Surcharge', 'Status'];

// Subtable component
function HorizontalSubtable({
  title,
  fields,
  firstDetails,
}: {
  title: string;
  fields: string[];
  firstDetails: Record<string, string>;
}) {
  return (
    <>
    
    <Box mb={4} className="innerTable">
      <Table size="small">
        <TableHead>
          <TableRow>
            {fields.map((field) => (
              <TableCell key={field} sx={{ fontWeight: 'bold' }}>{field}</TableCell>
            ))}
          </TableRow>
        </TableHead>
        <TableBody>
          <TableRow>
            {fields.map((field) => (
              <TableCell
              key={field}
              className={
                field === 'Status'
                  ? firstDetails[field] === 'Active'
                    ? 'ActiveClass'
                    : 'InActiveClass'
                  : undefined
              }
            >
              {firstDetails[field] || '-'}
            </TableCell>
            ))}
          </TableRow>
        </TableBody>
      </Table>
    </Box>
    </>
  );
}
 
// Expandable row
function Row({ row }: { row: ReturnType<typeof createData> }) {
  const [open, setOpen] = React.useState(false);

  return (
    <>
    
      <TableRow>
        <TableCell>{row.serviceName}</TableCell>
        <TableCell>{row.countryMarket}</TableCell>
        <TableCell>{row.subscriptionPlan}</TableCell>
        <TableCell>{row.supplier}</TableCell>
        <TableCell>{row.hotelName}</TableCell>
        <TableCell>{row.roomType}</TableCell>
        <TableCell>{row.mealPlan}</TableCell>
        <TableCell align="center">
          <IconButton size="small" onClick={() => setOpen(!open)} className="viewBtn">
            {open ? (
              <>
                View Less <KeyboardArrowUpIcon />
              </>
            ) : (
              <>
                View More <KeyboardArrowDownIcon />
              </>
            )}
          </IconButton>
        </TableCell>
        <TableCell align="center">
                          <Tooltip />
                        </TableCell>
      </TableRow>

      <TableRow>
        <TableCell colSpan={9} style={{ paddingBottom: 0, paddingTop: 0 }} className='innerTableBG'>
          <Collapse in={open} timeout="auto" unmountOnExit>
            <Box margin={2}>
              <HorizontalSubtable
                title=""
                fields={fareFields}
                firstDetails={row.firstDetails}
              />
              <HorizontalSubtable
                title=""
                fields={airlineFields}
                firstDetails={row.firstDetails}
              />
            </Box>
          </Collapse>
        </TableCell>
      </TableRow>
    </>
  );
}

const ITEM_HEIGHT = 48;
  const ITEM_PADDING_TOP = 8;

  const MenuProp = {
    PaperProps: {
      style: {
        maxHeight: ITEM_HEIGHT * 4.5 + ITEM_PADDING_TOP,
        width: 250,
      },
    },
    anchorOrigin: {
      vertical: 'bottom' as 'bottom',
      horizontal: 'left' as 'left',
    },
    transformOrigin: {
      vertical: 'top' as 'top',
      horizontal: 'left' as 'left',
    },
    getContentAnchorEl: null, // Important for older versions of MUI v4; can omit for MUI v5+
  };


const label = [
  'Service Name',
  'Country / Market',
  'Subscription Plan',
  'Supplier',
  'Hotel Name',
  'Room Type',
  'Meal Plan',
];

// Main Component
export default function FlightServiceFees() {
  const [searchTerm, setSearchTerm] = useState('');
  const [filterBy, setFilterBy] = useState('');
  const handleSearch = () => {
      console.log(`Searching for "${searchTerm}" by ${filterBy}`);
    };
    const [labelName, setLabelName] = React.useState<string[]>([]);
  
    const labelChange = (event: SelectChangeEvent<typeof labelName>) => {
      const {
        target: { value },
      } = event;
      setLabelName(
        // On autofill we get a stringified value.
        typeof value === 'string' ? value.split(',') : value,
      );
    };

    
  return (
    <>
    <Box className="headingBtnWrap">
      <Typography variant="h1">Service Fees</Typography>
      <CreateButtonWithModal modalTitle="Service Fees">
        <Box className="modalFormWrap">
          <Box component="form" className="modalForm">
            <Box className="feeModalForm">
              <Typography variant="subtitle1" gutterBottom>Source</Typography>
              <Box className="feeInputFieldWrap">
              {['Service Name', 'Country / Market', 'Subscription Plan', 'Supplier' ].map((label) => (
                <div className="form-control inputFieldWrap" key={label}>
                  <label>{label}</label>
                  <input type="text" className="inputField" />
                </div>
              ))}
              </Box>
              <Typography variant="subtitle1" gutterBottom>Condition criteria</Typography>
              <Box className="feeInputFieldWrap">
              {['Hotel Name', 'Room Type', 'Meal Plan', 'Refundable / Non-Refundable', 'Star Rating' ].map((label) => (
                <div className="form-control inputFieldWrap" key={label}>
                  <label>{label}</label>
                  <input type="text" className="inputField" />
                </div>
              ))}
              </Box>
              <Typography variant="subtitle1" gutterBottom>Applicability Criteria</Typography>
              <Box className="feeInputFieldWrap">
              {['From Date', 'To Date', 'Booking Window', 'Minimum Stay', 'Maximum Stay' ].map((label) => (
                <div className="form-control inputFieldWrap" key={label}>
                  <label>{label}</label>
                  <input type="text" className="inputField" />
                </div>
              ))}
              </Box>
              <Typography variant="subtitle1" gutterBottom>Pricing Logic</Typography>
              <Box className="feeInputFieldWrap">
              {['Applicability On', 'Device Type', 'Services fees On', 'Service fee Type', 'Service fee Value', 'Payment Method Surcharge' ].map((label) => (
                <div className="form-control inputFieldWrap" key={label}>
                  <label>{label}</label>
                  <input type="text" className="inputField" />
                </div>
              ))}
              </Box>
            </Box>
          </Box>
          <Box display="flex" justifyContent="flex-end">
            <Button variant="contained" className='formBtn'>Save</Button>
          </Box>
        </Box>
        <BlackWhiteSwitch />
      </CreateButtonWithModal>
    </Box>
    <Box className="searchInputWrap">
      <Box
        display="flex"
        alignItems="center"
        gap={2}
        p={2}
        sx={{ width: '100%'}}
        className="searchInputFilterBox"
      >
        <FormControl sx={{ minWidth: 460 }}>
           <Select
            className='leftFilterBox'
            labelId="demo-multiple-checkbox-label"
            id="demo-multiple-checkbox"
            multiple
            value={labelName}
            onChange={labelChange}
            displayEmpty
            input={<OutlinedInput />}
            renderValue={(selected) => {
              if (selected.length === 0) {
                return 'Select';  // <-- Placeholder text
              }
              return selected.join(', ');
            }}
            MenuProps={MenuProp}
          >
            <MenuItem disabled value="">
            Select
            </MenuItem>
            {label.map((name) => (
              <MenuItem key={name} value={name} className='selectDropdown'>
                <Checkbox checked={labelName.includes(name)} />
                <ListItemText primary={name} />
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        {/* <TextField
          className="leftSearchFilter"
          fullWidth
          variant="outlined"
          placeholder="Search"
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          onKeyPress={(e) => {
            if (e.key === 'Enter') handleSearch();
          }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <IconButton onClick={handleSearch} edge="start">
                  <SearchIcon />
                </IconButton>
              </InputAdornment>
            ),
            endAdornment: (
              <Button
                variant="contained"
                className="searchBtn"
                color="primary"
                type="submit"
                sx={{ borderTopLeftRadius: 0, borderBottomLeftRadius: 0 }}
                onClick={handleSearch}
              >
                Search
              </Button>
            ),
          }}
        /> */}
        <Button
                variant="contained"
                className="searchBtn"
                color="primary"
                type="submit"
                sx={{ borderTopLeftRadius: 0, borderBottomLeftRadius: 0 }}
              >
                Search
              </Button>
      </Box>
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 2 }} className="dynamicInputFieldWrap">
        {labelName.map((item) => (
          <div key={item} className="form-control inputFieldWrap">
            <label style={{ marginBottom: '4px', fontWeight: '500' }}>{item}</label>
            <input
              type="text"
              // placeholder={`Enter ${item}`}
              className="inputField"
            />
          </div>
        ))}
      </Box>
    </Box>
    <Box>
      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Service Name</TableCell>
              <TableCell>Country / Market</TableCell>
              <TableCell>Subscription Plan</TableCell>
              <TableCell>Supplier</TableCell>
              <TableCell>Hotel Name</TableCell>
              <TableCell>Room Type</TableCell>
              <TableCell>Meal Plan</TableCell>
              <TableCell align="center">Actions</TableCell>
              <TableCell align="center">Audit Trail</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {rows.map((row, index) => (
              <Row key={index} row={row} />
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
    </>
  );
}
